import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  findGetParameter,
  getData,
  newPath,
  useInterval
} from "../../../components/utilities/UtilityFunctions";
import { postNotification } from "../../../components/utilities/Notifications";
import { handlePagingAll } from "../../../components/api/APIUtilities";
import "moment-timezone";
import EventPlacingsDefault from "./EventPlacings/EventPlacingsDefault";

export default function EventPlacings(props) {
  const {
    organize,
    activeTab,
    setActiveTab,
    event,
    setEvent,
    authContext,
    setResetModalOpen
  } = props;
  const navigate = useNavigate();

  var leagueId = findGetParameter("league");
  if (!leagueId) leagueId = findGetParameter("circuit");

  const [originalLoading, setOriginalLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [subLoading, setSubLoading] = useState(false);

  const [playerType, setPlayerType] = useState(
    event?.teamEvent ? "teamplayers" : "players"
  );

  const [players, setPlayers] = useState(null);
  const [playerDict, setPlayerDict] = useState({});
  const [player, setPlayer] = useState(null);
  const [showOverall, setShowOverall] = useState(false);
  const [placingDict, setPlacingDict] = useState({});

  const [league, setLeague] = useState(null);

  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
  const [refreshPlayers, setRefreshPlayers] = useState(false);

  const [placingsReady, setPlacingsReady] = useState(false);

  // Checks if placings are up to date
  const checkStatus = async () => {
    if (event) {
      try {
        const apiStatus = await getData(`/events/${event.id}/updatestatus`);
        if (apiStatus?.status === "ready") {
          setPlacingsReady(true);
        }
        if (apiStatus?.status === "processing") {
          setPlacingsReady(false);
        }
      } catch (err) {
        setPlacingsReady(false);
      }
    }
  };

  const sortPlayers = (playerArray) => {
    playerArray.sort((a, b) => {
      if (showOverall) {
        if (a.overallPlacing && !b.overallPlacing) return -1;
        if (!a.overallPlacing && b.overallPlacing) return 1;
        return a.overallPlacing - b.overallPlacing;
      }
      if (a.podNum && !b.podNum) return -1;
      if (!a.podNum && b.podNum) return 1;
      if (a.podNum < b.podNum) return -1;
      if (a.podNum > b.podNum) return 1;
      if (a.isPod && !b.isPod) return -1;
      if (!a.isPod && b.isPod) return 1;

      return a.placing - b.placing;
    });
    return playerArray;
  };

  const handleLoad = async (collection) => {
    try {
      setSubLoading(true);
      const fetchPlayers = async () => {
        const playerArray = await getData(
          `/events/${event?.id}/${
            collection ? collection.toLowerCase() : "/players"
          }`,
          {
            placings: true
          }
        );

        if (event.wcwEvent && event.podRound >= event.currentRound) {
          playerArray?.active?.sort((a, b) => {
            if (a.podNum && !b.podNum) return -1;
            if (!a.podNum && b.podNum) return 1;
            if (a.podNum < b.podNum) return -1;
            if (a.podNum > b.podNum) return 1;
            for (let i = 0; i < a?.total_metrics?.length; i++) {
              if (a.total_metrics[i]?.value > b.total_metrics[i]?.value) {
                return -1;
              }
              if (a.total_metrics[i]?.value < b.total_metrics[i]?.value) {
                return 1;
              }
            }
            return 0;
          });
        } else {
          playerArray?.active?.sort((a, b) => {
            if (a.placing > b.placing) {
              return -1;
            }
            if (a.placing < b.placing) {
              return 1;
            }
            return 0;
          });
        }

        let placing = 0;

        playerArray?.active?.forEach((p) => {
          placing++;
          if (event.wcwEvent && event.podRound >= event.currentRound) {
            p.placing = placing;
          }

          p.overall1 = p.overall_metrics?.[0]?.value || 0;
          p.overall2 = p.overall_metrics?.[1]?.value || 0;
          p.overallBattlePoints = p.overall_metrics?.[0]?.value || 0;

          if (p.softScores) {
            var softScores = {};
            p.softScores.forEach((softScore) => {
              softScores[softScore.name] = softScore;
            });
            p.softScores = softScores;
          }
        });

        return playerArray?.active;
      };

      const fetchPlacings = async () => {
        if (!leagueId) return [];
        return await handlePagingAll("/eventplacings", {
          eventId: event.id,
          leagueId: leagueId
        });
      };

      const fetchLeague = async () => {
        if (!leagueId) return null;
        var returnObj;
        try {
          returnObj = await getData(`/leagues/${leagueId}`);
        } catch (err) {
          console.error(err);
        }
        return returnObj;
      };

      async function parallel() {
        const taskPlayers = fetchPlayers();
        const taskPlacings = fetchPlacings();
        const taskLeague = fetchLeague();

        return {
          players: await taskPlayers,
          placings: await taskPlacings,
          league: await taskLeague
        };
      }

      var parallelData = await parallel();
      if (parallelData.league) setLeague(parallelData.league);

      // Adds team info for individual players in team/doubles events
      if (
        parallelData?.players?.length &&
        playerType === "players" &&
        (event?.doublesEvent || event?.teamEvent)
      ) {
        let apiTeams = null;
        try {
          apiTeams = await getData(`/events/${event?.id}/teamPlayers`, {
            placings: false
          });
        } catch (err) {
          postNotification("error");
        }

        if (apiTeams?.active) {
          parallelData.players.forEach((player) => {
            apiTeams.active.forEach((team) => {
              if (player?.teamPlayerId === team?.id) {
                player.team = { ...team };
              }
            });
          });
        }
      }

      var newPods = {};
      var newPlayerDict = {};
      parallelData.players.forEach((p) => {
        if (
          (event.topCut ||
            event.bracketPairings ||
            (event.wcwEvent && event.currentRound >= event.podRound)) &&
          ((p.podNum === undefined && !newPods["undefined"]) ||
            !newPods[p.podNum])
        ) {
          newPods[p.podNum !== undefined ? p.podNum : "undefined"] = {
            isPod: true,
            podNum: p.podNum
          };
        }
        newPlayerDict[p.id] = p;
      });

      if (Object.keys(newPods)?.length > 0) {
        if (
          event.topCut ||
          (event.wcwEvent && event.currentRound >= event.podRound)
        ) {
          if (newPods[1]) {
            newPods[1].label =
              event.wcwEvent || event.doubleElimination
                ? "Championship Bracket"
                : "Top Cut";
          }
          if (event.doubleElimination) {
            if (newPods[2]) {
              newPods[2].label = "Elimination Bracket";
            }
            if (newPods[3]) {
              newPods[3].label = "Eliminated Bracket";
            }
          }
        } else if (
          event.pairingStyle === "single elimination" ||
          event.pairingStyle === "double elimination"
        ) {
          if (newPods[1]) {
            newPods[1].label = "Championship Bracket";
          }
          if (newPods[2]) {
            newPods[2].label =
              event.pairingStyle === "single elimination"
                ? "Eliminated Bracket"
                : "Elimination Bracket";
          }
          if (newPods[3]) {
            newPods[3].label = "Eliminated Bracket";
          }
        }

        parallelData.players.push(...Object.values(newPods));
      }

      setPlayerDict(newPlayerDict);
      let _sortedPairings = sortPlayers(parallelData.players);
      setPlayers(_sortedPairings);

      if (parallelData.placings?.length > 0) {
        var newPlacingDict = {};
        parallelData.placings.forEach((placing) => {
          newPlacingDict[placing.playerId] = placing;
        });
        setPlacingDict(newPlacingDict);
      }
    } catch (err) {
      postNotification("error", err);
    }
    setSubLoading(false);
    setOriginalLoading(false);
  };

  useEffect(() => {
    if (event) {
      if (!organize && event.hidePlacings) {
        var path = newPath({
          active_tab: "overview"
        });
        setActiveTab(0);
        navigate(path, { replace: false });
        return;
      }
      handleLoad(event?.teamEvent ? "teamplayers" : "players");
    }
    // eslint-disable-next-line
  }, [activeTab, event]);

  useEffect(() => {
    if (refreshPlayers) {
      handleLoad(playerType);
      setRefreshPlayers(false);
    }
    // eslint-disable-next-line
  }, [refreshPlayers]);

  // handle showOverall toggle
  useEffect(() => {
    const check = async () => {
      if (players) {
        let _players = sortPlayers(players);
        setPlayers([..._players]);
        setSubLoading(false);
      }
    };
    check();
    // eslint-disable-next-line
  }, [showOverall]);

  useEffect(() => {
    if (!originalLoading) {
      handleLoad(playerType);
    }
    // eslint-disable-next-line
  }, [playerType]);

  useEffect(() => {
    const go = async () => {
      await checkStatus();
    };
    go();
    // eslint-disable-next-line
  }, []);

  useInterval(() => {
    if (!placingsReady) {
      checkStatus();
    }
  }, 15000);

  return (
    <EventPlacingsDefault
      event={event}
      setEvent={setEvent}
      playerType={playerType}
      setPlayerType={setPlayerType}
      showOverall={showOverall}
      setShowOverall={setShowOverall}
      subLoading={subLoading}
      setSubLoading={setSubLoading}
      players={players}
      authContext={authContext}
      organize={organize}
      loading={loading}
      setLoading={setLoading}
      optionsAnchorEl={optionsAnchorEl}
      setOptionsAnchorEl={setOptionsAnchorEl}
      setPlayer={setPlayer}
      placingDict={placingDict}
      league={league}
      player={player}
      playerDict={playerDict}
      setRefreshPlayers={setRefreshPlayers}
      setResetModalOpen={setResetModalOpen}
      placingsReady={placingsReady}
    />
  );
}
